import React, { useState } from 'react';
import SchemaForm, { FormField } from '../../components/Forms/SchemaForm';
import VerificationPageLayout from '../../components/Layout/VerificationPageLayout';
import VerificationResultDisplay from '../../components/Verification/VerificationResultDisplay';
import {
    consumerTraceService,
    traceSchema,
    ConsumerTracePayload,
    ConsumerMatchItem,
    ConsumerTraceResponse,
} from '../../services/verificationSystemServices/consumerTraceService';
import {
    Box,
    Alert,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
} from '@mui/material';
import ActionButton from '../../components/Button/ActionButton';

const ConsumerTrace = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [matches, setMatches] = useState<ConsumerMatchItem[] | null>(null);
    const [fetchingResultFor, setFetchingResultFor] = useState<string | null>(null);
    const [report, setReport] = useState<ConsumerTraceResponse | null>(null);
    const [searchPayload, setSearchPayload] = useState<ConsumerTracePayload | null>(null);

    const fetchReport = async (match: ConsumerMatchItem) => {
        setFetchingResultFor(match.enquiry_result_id);
        setError(null);
        try {
            const response = await consumerTraceService.getResult({
                enquiryId: parseInt(match.enquiry_id),
                enquiryResultId: parseInt(match.enquiry_result_id),
                yourReference: searchPayload?.yourReference || undefined,
                voucherCode: searchPayload?.voucherCode || undefined,
            });
            setReport(response);
            setMatches(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || 'Failed to fetch Consumer Trace report');
        } finally {
            setFetchingResultFor(null);
        }
    };

    const handleSubmit = async (data: any) => {
        setIsLoading(true);
        setError(null);
        setMatches(null);
        setReport(null);

        const payload = consumerTraceService.createPayload(data);
        setSearchPayload(payload);

        try {
            const response = await consumerTraceService.getMatch(payload);

            if (response.matches.length === 1) {
                await fetchReport(response.matches[0]);
            } else {
                setMatches(response.matches);
            }
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || 'An error occurred during Consumer Trace match');
        } finally {
            setIsLoading(false);
        }
    };

    const formSchema: FormField[] = [
        {
            name: 'idNo',
            label: 'ID Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
            maxLength: 13,
        },
        {
            name: 'passportNo',
            label: 'Passport Number / Other ID',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
        {
            name: 'firstName',
            label: 'First Name',
            type: 'text',
            gridProps: { xs: 12, md: 4 },
            required: false,
        },
        {
            name: 'surname',
            label: 'Surname',
            type: 'text',
            gridProps: { xs: 12, md: 4 },
            required: false,
        },
        {
            name: 'dob',
            label: 'Date of Birth',
            type: 'date',
            gridProps: { xs: 12, md: 4 },
            required: false,
        },
        {
            name: 'voucherCode',
            label: 'Voucher Code',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
        {
            name: 'yourReference',
            label: 'Your Reference',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
    ];

    return (
        <VerificationPageLayout title="Consumer Trace">

            {/* Form is always visible */}
            <Box mb={4}>
                <SchemaForm
                    schema={formSchema}
                    onSubmit={handleSubmit}
                    submitLabel={isLoading ? 'Searching...' : 'Trace Consumer'}
                    validationSchema={traceSchema}
                />
            </Box>

            {(isLoading || fetchingResultFor !== null) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                    <CircularProgress sx={{ color: '#2D4677' }} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Multiple matches — let the user pick */}
            {matches && matches.length > 1 && !report && (
                <Box mb={4}>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        Multiple consumers matched your search. Select the correct record to fetch the full report.
                    </Typography>
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #dce6f5', borderRadius: '12px', overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#EFF3FB' }}>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Consumer ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Full Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>ID Number</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date of Birth</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Gender</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matches.map((match) => (
                                    <TableRow key={match.enquiry_result_id} hover sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.consumer_id ?? '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a1a2e' }}>
                                            {[match.first_name, match.surname].filter(Boolean).join(' ') || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.id_number ?? '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.birth_date ?? '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.gender ?? '—'}</TableCell>
                                        <TableCell align="right">
                                            <ActionButton
                                                size="small"
                                                disabled={fetchingResultFor !== null}
                                                onClick={() => fetchReport(match)}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    py: 0.75,
                                                    px: 2,
                                                }}
                                            >
                                                {fetchingResultFor === match.enquiry_result_id ? 'Fetching…' : 'Get Report'}
                                            </ActionButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Final report */}
            {report && !isLoading && !fetchingResultFor && (
                <>
                    {(!matches || matches.length === 0) && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => { setReport(null); setMatches(null); }}
                            sx={{
                                mb: 2,
                                borderColor: '#2D4677',
                                color: '#2D4677',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { borderColor: '#1a365d', backgroundColor: 'rgba(45,70,119,0.04)' },
                            }}
                        >
                            ← Clear Result
                        </Button>
                    )}
                    <VerificationResultDisplay
                        title="Consumer Trace"
                        requestId={report.log_id}
                        success={report.success}
                        message={report.message}
                        data={report.report_data?.Consumer ?? report.report_data ?? null}
                    />
                </>
            )}
        </VerificationPageLayout>
    );
};

export default ConsumerTrace;
